import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Send, ArrowUpRight } from 'lucide-react';
import { useLang } from '../context/LangContext';

import { normalizeCmsImages } from '../api/config';
import { resolveMediaUrl } from '../utils/media';
import { cmsApi, settingsApi } from '../api/requests';

export default function Footer() {
  const [settings, setSettings] = useState({ telegramUrl: '', instagramUrl: '' });
  const [headerCms, setHeaderCms] = useState(null);
  const { t } = useLang();

  useEffect(() => {
    Promise.all([
      settingsApi.getPublic(),
      cmsApi.getSection('header')
    ]).then(([s, h]) => { setSettings(s.data); setHeaderCms(normalizeCmsImages(h.data)); })
      .catch(() => {});
  }, []);

  const logoText = headerCms?.logoText || 'CREO';
  const logoAccent = headerCms?.logoAccent || 'FARM';
  const quickLinks = [
    { label: t('nav.collection'), path: '/catalog' },
    { label: t('nav.about'), path: '/#about' },
    { label: t('nav.faq'), path: '/#faq' },
    { label: t('nav.customOrder'), path: '/custom-order' },
  ];
  const legalLinks = [
    { label: 'Impressum', path: '/impressum' },
    { label: 'Datenschutz', path: '/datenschutz' },
    { label: 'AGB', path: '/agb' },
    { label: 'Widerrufsbelehrung', path: '/widerruf' },
  ];

  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              {headerCms?.logoImage
                ? <img src={resolveMediaUrl(headerCms.logoImage)} alt={logoText} className="h-10 md:h-12" />
                : <span className="text-3xl md:text-4xl font-bold tracking-tight text-white font-['Outfit']">{logoText}<span className="text-indigo-400">{logoAccent}</span></span>}
            </Link>
            <p className="text-zinc-400 text-lg max-w-md mb-8">{t('footer.brandDesc')}</p>
            <div className="flex items-center space-x-4">
              {settings.telegramUrl && (
                <a href={settings.telegramUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-telegram"
                  className="group flex items-center space-x-2 px-4 py-2 bg-white/5 hover:bg-indigo-500/20 border border-white/10 hover:border-indigo-500/50 rounded-full transition-all">
                  <Send className="w-5 h-5 text-indigo-400" /><span className="text-sm text-zinc-300 group-hover:text-white">Telegram</span>
                  <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-indigo-400" />
                </a>
              )}
              {settings.instagramUrl && (
                <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
                  className="group flex items-center space-x-2 px-4 py-2 bg-white/5 hover:bg-indigo-500/20 border border-white/10 hover:border-indigo-500/50 rounded-full transition-all">
                  <Instagram className="w-5 h-5 text-indigo-400" /><span className="text-sm text-zinc-300 group-hover:text-white">Instagram</span>
                  <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-indigo-400" />
                </a>
              )}
            </div>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-6 font-['Outfit']">{t('footer.quickLinks')}</h4>
            <ul className="space-y-4">
              {quickLinks.map((link, i) => (
                <li key={i}><Link to={link.path} data-testid={`footer-quick-link-${i}`} className="text-zinc-400 hover:text-indigo-400 transition-colors">{link.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-6 font-['Outfit']">{t('footer.legal')}</h4>
            <ul className="space-y-4">
              {legalLinks.map((link, i) => (
                <li key={i}><Link to={link.path} data-testid={`footer-legal-link-${i}`} className="text-zinc-400 hover:text-indigo-400 transition-colors">{link.label}</Link></li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-zinc-500 text-sm">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
            <p className="text-zinc-600 text-xs">{t('footer.tagline')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
